/// <amd-module name="Environments/EnvironmentsSecurity/Components/EnvironmentsSecurityComponent" />
import React = require("react");

import * as Contracts from "DistributedTask/Client/Contracts/TaskAgent";
import { PipelinesAuthorizationComponent } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Components/PipelinesAuthorizationComponent";
import { PageContextHelper } from "Environments/Client/PageContextHelper";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { getCurrentEnvironmentIdFromRoute, getEnvironmentsPageUrl, getEnvironmentPageUrl, getEnvironmentsSecurityPageUrl } from "Environments/EnvironmentsCommon/Link";
import { PageKey, getResourceId, getScopeId } from "Environments/EnvironmentsSecurity/Common/Utils";
import { addBreadcrumbs } from "Environments/Breadcrumb/EnvironmentsHubBreadcrumbService";
import { EnvironmentSecurityDialogConstants, environmentSecurityUrl } from "Environments/EnvironmentsSecurity/Constants";
import { EnvironmentsSecurityActionsCreator } from "Environments/EnvironmentsSecurity/ActionsCreators/EnvironmentsSecurityActionsCreator";
import { EnvironmentsSecurityStore } from "Environments/EnvironmentsSecurity/Stores/EnvironmentsSecurityStores";
import { EnvironmentsSecuritySource } from "Environments/EnvironmentsSecurity/Sources/EnvironmentsSecuritySource";
import * as Resources from "Environments/EnvironmentsSecurity/Resources";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

import { VssComponent, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { onClickFPS } from "VSS/Platform/FPS";

import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { Area, FeatureNames } from "Environments/EnvironmentsSecurity/Common/Utils";

import {IRoleAssignmentProps, RoleAssignment } from "VSS/Features/SecurityUI/Roles/RoleAssignment";

import "VSS/LoaderPlugins/Css!Environments/EnvironmentsSecurity/environments-security";

export interface IEnvironmentsSecurityComponentState extends IEnvironmentBaseComponentState {
    environment: Contracts.EnvironmentInstance | undefined;
}

export class EnvironmentsSecurityComponent extends EnvironmentBaseComponent<IEnvironmentBaseComponentProperties, IEnvironmentsSecurityComponentState> {

    public constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        let environmentId = getCurrentEnvironmentIdFromRoute(context.pageContext);
        publishTelemetry(context.pageContext, Area, FeatureNames.EnvironmentSecurityView, { "EnvironmentId": environmentId ? "" : environmentId.toString()});
        // if the environmentId is undefined, then it is for Environments page
        this._environmentId = (environmentId !== -1) ? environmentId : undefined;
        this.state = {
            environment: undefined,
            errorMessages: {}
        };

        if (this._environmentId) {
            this._actionCreator = ActionCreatorManager.CreateActionCreator<EnvironmentsSecurityActionsCreator>(EnvironmentsSecurityActionsCreator, this.getInstanceId(context));
            this._store = StoreManager.CreateStore<EnvironmentsSecurityStore>(EnvironmentsSecurityStore, this.getInstanceId(context));
            this._store.addChangedListener(this._onEnvironmentsSecurityStoreChange);
            this._actionCreator.getEnvironmentById(this.context.pageContext, this._environmentId, false);
            let source = EnvironmentsSecuritySource.getInstance(context.pageContext);
            this._canRestrictAccess = source.checkAdminPermission(this._environmentId.toString());
            this._canOpenAccess = source.checkProjectAdminPermission();
        }
    }

    public getInstanceId(context: IVssComponentContext): string {
        return "environments-security-id-" + this.componentId;
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (!this._environmentId) {
            this._addBreadcrumbs();
        }
    }

    public render(): JSX.Element {

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    {this._getHeader()}
                    {this._getErrorMessage()}
                    <div className="pipelines-environments-security page-content page-content-top">
                        {!this._getErrorMessage() && this._getEnvironmentsRolePermissions()}
                        <div className="card-spacer flex-grow" />
                        {!this._getErrorMessage() && this._getPipelinesPermissions()}
                    </div>
                </Page>
            </Surface>
        )
    }

    private _getErrorMessage(): React.ReactNode {
        if (this._messagesStore.getMessage(PageKey.EnvironmentsSecurityView)) {
            return (
                <div className="page-content page-content-top">
                    {this.getErrorMessageView(PageKey.EnvironmentsSecurityView)}
                </div>
            )
        }
    }

    private _getHeader(): JSX.Element {
        const description = this.state.environment ? this.state.environment.name : Resources.EnvironmentsSubtitleText
        let environmentSecurityTitle: JSX.Element = (
            <Header
                title={Resources.SecurityTitleText}
                titleSize={TitleSize.Large}
                description={description}
                backButtonProps={{ onClick: this._onBackButtonClick }}
            />
        );

        return environmentSecurityTitle;
    }

    private _onBackButtonClick = (): void => {
        onClickFPS(this.context.pageContext, this._getEnvironmentSecurityUrl(), true);
    }

    private _getEnvironmentSecurityUrl(): string {
        if (this._environmentId && this.state.environment) {
            return getEnvironmentPageUrl(this.context.pageContext, this._environmentId)
        }

        return getEnvironmentsPageUrl(this.context.pageContext);
    }

    private _getEnvironmentsRolePermissions = (): JSX.Element| null => {
            const roleAssignmentProps: IRoleAssignmentProps = {
                serviceInstanceId: undefined,
                scopeId: getScopeId(this.context.pageContext, this._environmentId),
                userId: PageContextHelper.getCurrentUserId(this.context.pageContext) as string,
                resourceId: getResourceId(this.context.pageContext, this._environmentId),
                canEdit: true,
                manageRolesPermission: EnvironmentSecurityDialogConstants.ManageRolePermissions,
                noPermissionMessage: Resources.NoPermissionsToManageSecurity,
                showAvatars: true,
                enableToggleInheritence: this._environmentId ? true : false,
                identityTypes: ["user", "group", "servicePrincipal"],
                operationScopes: ["ims", "source"]
            };
    
            return (!this._environmentId || this.state.environment) ?  (
                <Card className="bolt-table-card" titleProps={{ text: Resources.UserSecurityTitle }}>
                    <RoleAssignment {...roleAssignmentProps} />
                </Card>
            ) : null;
    }

    private _getPipelinesPermissions = (): React.ReactNode => {
        let pipelinesPermissionsComponent: React.ReactNode = undefined;
        if (this._environmentId) {
            pipelinesPermissionsComponent = (
                <PipelinesAuthorizationComponent
                    resourceId={this._environmentId.toString()}
                    resourceType="environment"
                    noAuthorizedPipelinesViewLearnMoreLink={environmentSecurityUrl}
                    noRestrictionsViewLearnMoreLink={environmentSecurityUrl} 
                    canOpenAccess={this._canOpenAccess}
                    canRestrictAccess={this._canRestrictAccess} />
            );
        }

        return pipelinesPermissionsComponent;
    }

    private _onEnvironmentsSecurityStoreChange = (): void => {
        const environment = this._store.getEnvironment();

        // Add breadcrumb for environment security view
        this._addBreadcrumbs(environment);

        // Re-render the page using the acquired environment
        this.setState({ environment });
    }

    private _addBreadcrumbs(environment?: Contracts.EnvironmentInstance): void {
        const { pageContext } = this.context;
        if (environment) {
            const environmentPageUrl = getEnvironmentPageUrl(pageContext, environment.id);
            const environmentSecurityPageUrl = getEnvironmentsSecurityPageUrl(pageContext, environment.id);
            addBreadcrumbs(
                pageContext, 
                { name: environment.name, url: environmentPageUrl },
                { name: Resources.SecurityTitleText, url: environmentSecurityPageUrl }
            );
        }
        else {
            const environmentsPageUrl = getEnvironmentsPageUrl(pageContext);
            addBreadcrumbs(pageContext, { name: Resources.SecurityTitleText, url: environmentsPageUrl });
        }
    }

    private _environmentId: number | undefined;
    private _store: EnvironmentsSecurityStore;
    private _actionCreator: EnvironmentsSecurityActionsCreator;
    private _canOpenAccess: boolean;
    private _canRestrictAccess: boolean;
}

VssComponent.register("environmentsSecurityView", EnvironmentsSecurityComponent);